import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/utils';

const messageVariants = cva(
  'group flex w-full max-w-full flex-col gap-2 overflow-hidden',
  {
    variants: {
      from: {
        user: 'is-user ml-auto justify-end',
        assistant: 'is-assistant group/assistant',
        error: 'is-error',
      },
    },
    defaultVariants: {
      from: 'assistant',
    },
  },
);

export type MessageProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof messageVariants>;

export const Message = React.forwardRef<HTMLDivElement, MessageProps>(
  ({className, from, ...props}, ref) => (
    <div
      ref={ref}
      className={cn(messageVariants({from}), className)}
      {...props}
    />
  ),
);
Message.displayName = 'Message';

export type MessageContentProps = React.HTMLAttributes<HTMLDivElement>;

export const MessageContent = React.forwardRef<
  HTMLDivElement,
  MessageContentProps
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex w-fit min-w-0 max-w-full flex-col gap-2 overflow-hidden text-sm',
      'group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:bg-muted group-[.is-user]:px-4 group-[.is-user]:py-3',
      'group-[.is-assistant]:text-foreground',
      'group-[.is-error]:rounded-lg group-[.is-error]:border group-[.is-error]:border-destructive/50 group-[.is-error]:bg-destructive/10 group-[.is-error]:px-4 group-[.is-error]:py-3 group-[.is-error]:text-destructive',
      className,
    )}
    {...props}
  />
));
MessageContent.displayName = 'MessageContent';

export type MessageResponseProps = React.HTMLAttributes<HTMLDivElement>;

export const MessageResponse = React.memo(
  React.forwardRef<HTMLDivElement, MessageResponseProps>(
    ({className, ...props}, ref) => (
      <div
        ref={ref}
        className={cn(
          'prose prose-sm max-w-full overflow-hidden wrap-break-word [&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
          '[&_pre]:max-w-full [&_pre]:overflow-x-auto [&_code]:break-all [&_p]:wrap-break-word [&_li]:wrap-break-word',
          className,
        )}
        {...props}
      />
    ),
  ),
);
MessageResponse.displayName = 'MessageResponse';

export type MessageActionsProps = React.HTMLAttributes<HTMLDivElement>;

export const MessageActions = React.forwardRef<
  HTMLDivElement,
  MessageActionsProps
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-1', className)}
    {...props}
  />
));
MessageActions.displayName = 'MessageActions';
